import { type ReactElement, useMemo } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import LoadingButton from "@atlaskit/button/loading-button";
import Form from "@atlaskit/form";
import SectionMessage, {
    SectionMessageAction
} from "@atlaskit/section-message";

import {
    BottomNav,
    DisabledSiteNameInput,
    FormFooter,
    FormHeader,
    FormSection
} from "../../ui";

export interface DisabledSiteNameInputFormProps {
    value: string;
    onSubmit: (value: DisabledSiteNameInputFormData) => void;
    readonly newSite?: (value: boolean) => void;
    readonly renderPreview?: (value: string) => ReactElement;
    readonly error?: {
        isConflictError?: boolean;
    };
    readonly flags?: {
        lockedInSiteName?: string;
        orderSlug?: string;
    };
    readonly provisionableProductCard?: ReactElement;
    readonly organizationCard?: ReactElement;
}

export interface DisabledSiteNameInputFormData {
    siteName: string;
}

export const DisabledSiteNameInputForm = ({
    newSite,
    onSubmit,
    renderPreview,
    error,
    flags,
    value,
    provisionableProductCard,
    organizationCard
}: DisabledSiteNameInputFormProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get("orderId") || "";
    const formHeaderTitle = useMemo(
        () => (newSite ? "Name your site" : "Site confirmation"),
        [newSite]
    );
    const formHeaderDescription = useMemo(() => {
        return newSite
            ? "Your site name is included in the URL, so be sure the name is something your teams will recognize. Don't worry! You can always change it later."
            : "You selected this site during checkout, so that's where your software will be installed. Select 'Confirm' to continue.";
    }, [newSite]);
    const submitMessage = useMemo(
        () => (newSite ? "Create site" : "Confirm"),
        [newSite]
    );

    const postOrderConflictErrorLink = (
        <SectionMessageAction
            onClick={() => {
                createAnalyticsEvent({
                    eventName: "postOrderConflictError",
                    eventComponent: "direct-buy-site-setup",
                    event: "clicked",
                    actionSubject: "postOrderConflictErrorLink",
                    eventType: "ui",
                    attributes: {
                        orderId,
                        lockedInSiteName: flags?.lockedInSiteName
                    }
                }).fire("signup");
                window.open("https://support.atlassian.com/contact/", "_blank");
            }}
        >
            Contact support
        </SectionMessageAction>
    );
    const lockedInSiteMessage = () => (
        <SectionMessage
            testId="locked-in-post-order-site-message"
            appearance="information"
        >
            This site already includes apps from your order. You’re about to set
            up your apps on this site.
        </SectionMessage>
    );
    const postOrderConflictError = (
        conflictSiteName?: string,
        orderId?: string
    ) => (
        <SectionMessage
            testId="locked-in-post-order-conflict-error-message"
            appearance="error"
            title="We’re unable to set up your app"
            actions={[postOrderConflictErrorLink]}
        >
            {`${
                conflictSiteName || "unknown"
            } already has apps from this order. To continue, contact support and include your order number: `}
            <p>
                <strong>{flags?.orderSlug || orderId}</strong>
            </p>
        </SectionMessage>
    );

    return (
        <Form<DisabledSiteNameInputFormData> onSubmit={onSubmit}>
            {({ formProps, submitting }) => {
                return (
                    <form
                        {...formProps}
                        data-testid="disabled-site-name-input-form"
                    >
                        <FormHeader
                            title={formHeaderTitle}
                            description={
                                !!error?.isConflictError
                                    ? ""
                                    : formHeaderDescription
                            }
                        />
                        {!!error?.isConflictError && (
                            <FormSection>
                                {postOrderConflictError(
                                    flags?.lockedInSiteName,
                                    orderId
                                )}
                            </FormSection>
                        )}
                        {!!provisionableProductCard && provisionableProductCard}
                        {!!organizationCard && organizationCard}
                        <FormSection>
                            <DisabledSiteNameInput
                                value={value}
                                fieldName="siteName"
                                fieldLabel="Your site"
                                renderPreview={renderPreview}
                            />
                        </FormSection>
                        {!error?.isConflictError && (
                            <FormSection>{lockedInSiteMessage()}</FormSection>
                        )}
                        <FormFooter>
                            <LoadingButton
                                type="submit"
                                appearance="primary"
                                shouldFitContainer
                                isLoading={submitting}
                                isDisabled={!!error?.isConflictError}
                            >
                                {submitMessage}
                            </LoadingButton>
                        </FormFooter>
                        {newSite ? (
                            <BottomNav normalText="You can also">
                                <Button
                                    appearance="link"
                                    spacing="none"
                                    onClick={() => newSite(true)}
                                >
                                    add a new site
                                </Button>
                            </BottomNav>
                        ) : null}
                    </form>
                );
            }}
        </Form>
    );
};
